import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { APP_NAME } from 'app-config'

import {
  selectCurrentVariantCohort,
  selectIsGrowthbookReady,
  selectLanguage,
} from 'root-redux/selects/common'
import { selectUUID, selectUserCountryCode } from 'root-redux/selects/user'

import { useGetPageInfo } from 'hooks/useGetPageInfo'

import { getInitialPageRequestTimings } from 'helpers/initialPagePerformance'
import {
  getDocumentMetrics,
  getFirstScreenReadyEventProperties,
  getNavigationEntry,
  getPaintReadyMetrics,
  getRequestMetrics,
  waitForNextPaint,
} from 'helpers/initialPageRenderMetrics'
import { captureInitialPageMetric } from 'helpers/sentryScope'

import { eventLogger } from 'services/eventLogger.service'

type TInitialPageRenderMetricParams = {
  isFirstScreenReady: boolean
}

type TFirstScreenReadyEvent = Record<string, unknown>

type TFirstPageShownEvent = {
  screenName: string
  firstScreenReadyEvent: TFirstScreenReadyEvent | null
}

export const useInitialPageRenderMetric = ({
  isFirstScreenReady,
}: TInitialPageRenderMetricParams): void => {
  const cohort = useSelector(selectCurrentVariantCohort)
  const locale = useSelector(selectLanguage)
  const country = useSelector(selectUserCountryCode)
  const uuid = useSelector(selectUUID)
  const isGrowthbookReady = useSelector(selectIsGrowthbookReady)
  const { currentPageId } = useGetPageInfo()

  const hasSentDocumentMetrics = useRef(false)
  const hasSentRequestMetrics = useRef(false)
  const hasSentFirstScreenMetric = useRef(false)
  const [pendingFirstPageShownEvent, setPendingFirstPageShownEvent] =
    useState<TFirstPageShownEvent | null>(null)

  const pageViewContext = useMemo(
    () => ({
      app: APP_NAME,
      screen: currentPageId,
      cohort,
      locale,
      country: country || '',
    }),
    [cohort, country, currentPageId, locale],
  )

  useEffect(() => {
    if (
      hasSentDocumentMetrics.current ||
      !uuid ||
      !currentPageId ||
      !cohort ||
      !locale
    ) {
      return
    }

    const navigationEntry = getNavigationEntry()

    if (!navigationEntry) return

    hasSentDocumentMetrics.current = true

    getDocumentMetrics(navigationEntry).forEach(({ name, value }) => {
      captureInitialPageMetric({
        name,
        value,
        uuid,
        ...pageViewContext,
      })
    })
  }, [cohort, currentPageId, locale, pageViewContext, uuid])

  useEffect(() => {
    if (
      hasSentRequestMetrics.current ||
      !uuid ||
      !currentPageId ||
      !cohort ||
      !locale
    ) {
      return
    }

    const requestTimings = getInitialPageRequestTimings()

    if (!requestTimings.config || !requestTimings.variant) return

    hasSentRequestMetrics.current = true

    getRequestMetrics(requestTimings).forEach(({ name, value }) => {
      captureInitialPageMetric({
        name,
        value,
        uuid,
        ...pageViewContext,
      })
    })
  }, [cohort, currentPageId, locale, pageViewContext, uuid])

  useEffect(() => {
    if (
      hasSentFirstScreenMetric.current ||
      !isFirstScreenReady ||
      !uuid ||
      !currentPageId ||
      !cohort ||
      !locale
    ) {
      return undefined
    }

    const captureFirstScreenReadyEvent = (): TFirstScreenReadyEvent | null => {
      try {
        const navigationEntry = getNavigationEntry()
        const paintReadyMetrics =
          navigationEntry && getPaintReadyMetrics(navigationEntry)

        if (!navigationEntry || !paintReadyMetrics) return null

        const requestTimings = getInitialPageRequestTimings()
        const firstScreenReadyProperties = getFirstScreenReadyEventProperties({
          navigationEntry,
          paintReadyMetrics,
          requestTimings,
        })

        captureInitialPageMetric({
          name: 'page_view.first_screen_ready_time_ms',
          value: firstScreenReadyProperties.first_screen_ready_time_ms,
          uuid,
          ...pageViewContext,
        })

        if (
          firstScreenReadyProperties.post_blocking_requests_ready_delay_ms !==
          null
        ) {
          captureInitialPageMetric({
            name: 'page_view.post_blocking_requests_ready_delay_ms',
            value:
              firstScreenReadyProperties.post_blocking_requests_ready_delay_ms,
            uuid,
            ...pageViewContext,
          })
        }

        return {
          ...pageViewContext,
          ...firstScreenReadyProperties,
        }
      } catch (error) {
        console.warn('capture first screen ready metric failed', error)
        return null
      }
    }

    return waitForNextPaint(() => {
      if (hasSentFirstScreenMetric.current) return

      hasSentFirstScreenMetric.current = true

      setPendingFirstPageShownEvent({
        screenName: currentPageId,
        firstScreenReadyEvent: captureFirstScreenReadyEvent(),
      })
    })
  }, [cohort, currentPageId, isFirstScreenReady, locale, pageViewContext, uuid])

  useEffect(() => {
    if (!pendingFirstPageShownEvent || !isGrowthbookReady) return

    const { screenName, firstScreenReadyEvent } = pendingFirstPageShownEvent

    eventLogger.logFirstPageShown(screenName, firstScreenReadyEvent)
    setPendingFirstPageShownEvent(null)
  }, [isGrowthbookReady, pendingFirstPageShownEvent])
}
